/**
 * ExpandableInteractiveWrapper
 *
 * Wraps interactive content (with its own header/body) with an expand control.
 * Modal has a single header (title + minimize); component renders in modal body as-is.
 * Fully responsive: touch-friendly expand button, modal full-viewport on mobile.
 *
 * Primary responsibilities:
 * - Inline: component + expand button
 * - Expanded: modal header (title + Minimize) + modal body (component only)
 */

'use client'

import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { IconArrowsMaximize } from '@tabler/icons-react'

import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { InteractiveExpandModal } from '@/components/ui/interactive-expand-modal'

export interface ExpandableInteractiveWrapperProps {
  title: string
  children: React.ReactNode
  className?: string
  buttonClassName?: string
}

export function ExpandableInteractiveWrapper({
  title,
  children,
  className,
  buttonClassName
}: ExpandableInteractiveWrapperProps) {
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState(false)

  return (
    <>
      <div className={cn('relative', className)}>
        {children}
        <div className={cn('absolute right-2 top-2 z-10', buttonClassName)}>
          <Button
            variant="secondary"
            size="icon"
            className="bg-background/80 hover:bg-background border-border min-h-[44px] min-w-[44px] rounded-full border shadow-md backdrop-blur-sm sm:size-8 sm:min-h-0 sm:min-w-0"
            onClick={() => setExpanded(true)}
            aria-label={t('Expand')}
            title={t('Expand')}
          >
            <IconArrowsMaximize className="size-4 sm:size-4" />
          </Button>
        </div>
      </div>

      <InteractiveExpandModal
        open={expanded}
        onOpenChange={setExpanded}
        title={title}
        minimizeLabel={t('Minimize')}
        bodyClassName="p-3 sm:p-4"
      >
        {children}
      </InteractiveExpandModal>
    </>
  )
}
